import { useEffect, useRef, useState } from "react";
import { parseAvatarState, type AvatarState, fetchUserAvatarConfig } from "@/lib/avatar";
import { getElementByAtomicNumber } from "@/lib/atomElements";
import { supabase } from "@/integrations/supabase/client";

export type AtomAvatarProps = {
  state: AvatarState | 'celebrating' | 'happy' | 'neutral' | 'sad' | 'excited' | 'thinking';
  size?: number;
  className?: string;
  animated?: boolean;
  speedMultiplier?: number;
  nucleusColorOverride?: string;
  nucleusOpacity?: number;
  hideFace?: boolean;
  chaoticSpeeds?: boolean;
};

function withHexAlpha(color: string, alphaHex: string) {
  if (/^#[0-9a-fA-F]{6}$/.test(color)) return `${color}${alphaHex}`;
  return color;
}

function drawAccessory(
  ctx: CanvasRenderingContext2D,
  acc: string,
  CX: number,
  CY: number,
  nucleusR: number,
  color: string,
) {
  ctx.save();

  switch (acc) {
    case "glasses": {
      const eyeY = CY - nucleusR * 0.15;
      const lensR = nucleusR * 0.28;
      const lensOffset = nucleusR * 0.38;
      ctx.strokeStyle = "rgba(255,255,255,0.95)";
      ctx.lineWidth = 1.5;
      ctx.fillStyle = "rgba(255,255,255,0.08)";
      ctx.beginPath();
      ctx.arc(CX - lensOffset, eyeY, lensR, 0, Math.PI * 2);
      ctx.fill();
      ctx.stroke();
      ctx.beginPath();
      ctx.arc(CX + lensOffset, eyeY, lensR, 0, Math.PI * 2);
      ctx.fill();
      ctx.stroke();
      ctx.beginPath();
      ctx.moveTo(CX - lensOffset + lensR, eyeY);
      ctx.lineTo(CX + lensOffset - lensR, eyeY);
      ctx.stroke();
      ctx.beginPath();
      ctx.moveTo(CX - lensOffset - lensR, eyeY);
      ctx.lineTo(CX - nucleusR * 0.95, eyeY - 2);
      ctx.stroke();
      ctx.beginPath();
      ctx.moveTo(CX + lensOffset + lensR, eyeY);
      ctx.lineTo(CX + nucleusR * 0.95, eyeY - 2);
      ctx.stroke();
      break;
    }
    case "hat": {
      const capY = CY - nucleusR * 0.85;
      const capW = nucleusR * 1.4;
      const capH = nucleusR * 0.7;
      const brimW = nucleusR * 1.8;
      const brimH = nucleusR * 0.2;
      ctx.fillStyle = "rgba(255,255,255,0.95)";
      ctx.fillRect(CX - capW / 2, capY - capH, capW, capH);
      ctx.fillRect(CX - brimW / 2, capY - brimH, brimW, brimH);
      ctx.fillStyle = color;
      ctx.fillRect(CX - capW / 2, capY - capH, capW, nucleusR * 0.15);
      break;
    }
    case "crown": {
      const crownBase = CY - nucleusR * 0.8;
      const crownH = nucleusR * 0.75;
      const crownW = nucleusR * 1.3;
      ctx.fillStyle = "#ffd700";
      ctx.beginPath();
      ctx.moveTo(CX - crownW / 2, crownBase);
      ctx.lineTo(CX - crownW / 2, crownBase - crownH * 0.6);
      ctx.lineTo(CX - crownW / 4, crownBase - crownH * 0.3);
      ctx.lineTo(CX, crownBase - crownH);
      ctx.lineTo(CX + crownW / 4, crownBase - crownH * 0.3);
      ctx.lineTo(CX + crownW / 2, crownBase - crownH * 0.6);
      ctx.lineTo(CX + crownW / 2, crownBase);
      ctx.closePath();
      ctx.fill();
      ctx.fillStyle = "white";
      ctx.beginPath();
      ctx.arc(CX, crownBase - crownH, 2.5, 0, Math.PI * 2);
      ctx.fill();
      ctx.fillStyle = "#ff6b6b";
      ctx.beginPath();
      ctx.arc(CX - crownW / 4, crownBase - crownH * 0.3, 2, 0, Math.PI * 2);
      ctx.fill();
      ctx.fillStyle = "#a78bfa";
      ctx.beginPath();
      ctx.arc(CX + crownW / 4, crownBase - crownH * 0.3, 2, 0, Math.PI * 2);
      ctx.fill();
      break;
    }
    case "lab": {
      const shirtY = CY + nucleusR * 0.7;
      const shirtW = nucleusR * 1.6;
      const shirtH = nucleusR * 1.1;
      ctx.fillStyle = "rgba(255,255,255,0.9)";
      ctx.beginPath();
      ctx.moveTo(CX - shirtW / 2, shirtY);
      ctx.lineTo(CX - shirtW / 2, shirtY + shirtH);
      ctx.lineTo(CX + shirtW / 2, shirtY + shirtH);
      ctx.lineTo(CX + shirtW / 2, shirtY);
      ctx.lineTo(CX + shirtW * 0.15, shirtY);
      ctx.lineTo(CX, shirtY + shirtH * 0.3);
      ctx.lineTo(CX - shirtW * 0.15, shirtY);
      ctx.closePath();
      ctx.fill();
      ctx.fillStyle = "rgba(200,220,255,0.6)";
      ctx.beginPath();
      ctx.moveTo(CX, shirtY + shirtH * 0.3);
      ctx.lineTo(CX - shirtW * 0.15, shirtY);
      ctx.lineTo(CX - shirtW * 0.35, shirtY + shirtH * 0.4);
      ctx.closePath();
      ctx.fill();
      ctx.beginPath();
      ctx.moveTo(CX, shirtY + shirtH * 0.3);
      ctx.lineTo(CX + shirtW * 0.15, shirtY);
      ctx.lineTo(CX + shirtW * 0.35, shirtY + shirtH * 0.4);
      ctx.closePath();
      ctx.fill();
      break;
    }
    case "headphones": {
      const arcR = nucleusR * 1.05;
      const cupW = nucleusR * 0.32;
      const cupH = nucleusR * 0.45;
      const cupY = CY - nucleusR * 0.1;
      ctx.strokeStyle = "rgba(40,40,40,0.95)";
      ctx.lineWidth = nucleusR * 0.22;
      ctx.lineCap = "round";
      ctx.beginPath();
      ctx.arc(CX, CY, arcR, Math.PI + 0.55, -0.55);
      ctx.stroke();
      ctx.fillStyle = "rgba(50,50,50,0.95)";
      ctx.beginPath();
      ctx.ellipse(CX - arcR, cupY, cupW, cupH, 0, 0, Math.PI * 2);
      ctx.fill();
      ctx.fillStyle = "rgba(80,80,80,0.8)";
      ctx.beginPath();
      ctx.ellipse(CX - arcR, cupY, cupW * 0.6, cupH * 0.6, 0, 0, Math.PI * 2);
      ctx.fill();
      ctx.fillStyle = "rgba(50,50,50,0.95)";
      ctx.beginPath();
      ctx.ellipse(CX + arcR, cupY, cupW, cupH, 0, 0, Math.PI * 2);
      ctx.fill();
      ctx.fillStyle = "rgba(80,80,80,0.8)";
      ctx.beginPath();
      ctx.ellipse(CX + arcR, cupY, cupW * 0.6, cupH * 0.6, 0, 0, Math.PI * 2);
      ctx.fill();
      break;
    }
    case "star": {
      const starCX = CX;
      const starCY = CY - nucleusR * 1.15;
      const outerR = nucleusR * 0.45;
      const innerR = nucleusR * 0.2;
      ctx.fillStyle = "#ffd700";
      ctx.beginPath();
      for (let i = 0; i < 10; i++) {
        const angle = (i * Math.PI) / 5 - Math.PI / 2;
        const r = i % 2 === 0 ? outerR : innerR;
        if (i === 0) ctx.moveTo(starCX + r * Math.cos(angle), starCY + r * Math.sin(angle));
        else ctx.lineTo(starCX + r * Math.cos(angle), starCY + r * Math.sin(angle));
      }
      ctx.closePath();
      ctx.fill();
      ctx.fillStyle = "rgba(255,255,255,0.6)";
      ctx.beginPath();
      ctx.arc(starCX - outerR * 0.2, starCY - outerR * 0.2, outerR * 0.2, 0, Math.PI * 2);
      ctx.fill();
      break;
    }
    case "lightning": {
      const boltX = CX + nucleusR * 0.55;
      const boltY = CY - nucleusR * 1.3;
      const boltH = nucleusR * 0.9;
      const boltW = nucleusR * 0.5;
      ctx.fillStyle = "#fbbf24";
      ctx.beginPath();
      ctx.moveTo(boltX + boltW * 0.3, boltY);
      ctx.lineTo(boltX - boltW * 0.1, boltY + boltH * 0.45);
      ctx.lineTo(boltX + boltW * 0.15, boltY + boltH * 0.45);
      ctx.lineTo(boltX - boltW * 0.3, boltY + boltH);
      ctx.lineTo(boltX + boltW * 0.5, boltY + boltH * 0.5);
      ctx.lineTo(boltX + boltW * 0.2, boltY + boltH * 0.5);
      ctx.closePath();
      ctx.fill();
      break;
    }
    default:
      break;
  }

  ctx.restore();
}

export function AtomAvatar({
  state,
  size = 112,
  className = "",
  animated = true,
  speedMultiplier = 1,
  nucleusColorOverride,
  nucleusOpacity,
  hideFace = false,
  chaoticSpeeds = false,
}: AtomAvatarProps) {
  const canvasRef = useRef<HTMLCanvasElement | null>(null);
  const rafRef = useRef<number | null>(null);
  const [userProfileAvatar, setUserProfileAvatar] = useState<AvatarState | null>(null);

  useEffect(() => {
    if (typeof state !== 'string') return;
    
    let isMounted = true;
    const fetchUserAvatar = async () => {
      try {
        const { data: { session } } = await supabase.auth.getSession();
        if (!session?.user?.id || !isMounted) return;

        const config = await fetchUserAvatarConfig(session.user.id);
        if (isMounted) {
          setUserProfileAvatar(config);
        }
      } catch (err) {
        console.error("Failed to load user avatar in AtomAvatar:", err);
      }
    };
    fetchUserAvatar();
    return () => {
      isMounted = false;
    };
  }, [state]);

  const defaultState: AvatarState = {
    elementN: 6,
    color: '#60A5FA',
    accessory: 'None',
  };

  const activeAvatarState = typeof state === 'string' 
    ? (userProfileAvatar || defaultState) 
    : state;

  // Resolve emotional state overrides
  let activeSpeedMultiplier = speedMultiplier;
  let activeChaoticSpeeds = chaoticSpeeds;
  let activeNucleusColor = nucleusColorOverride ?? activeAvatarState.color;
  let activeNucleusOpacity = nucleusOpacity;

  if (typeof state === 'string') {
    switch (state) {
      case 'celebrating':
        activeSpeedMultiplier = 1.2 * speedMultiplier;
        activeChaoticSpeeds = true;
        activeNucleusColor = '#FFEAA7';
        break;
      case 'excited':
        activeSpeedMultiplier = 2.0 * speedMultiplier;
        activeChaoticSpeeds = true;
        activeNucleusColor = '#FCD34D';
        break;
      case 'happy':
        activeSpeedMultiplier = 1.5 * speedMultiplier;
        activeChaoticSpeeds = false;
        break;
      case 'sad':
        activeSpeedMultiplier = 0.4 * speedMultiplier;
        activeChaoticSpeeds = false;
        activeNucleusColor = '#F87171';
        break;
      case 'thinking':
        activeSpeedMultiplier = 0.25 * speedMultiplier;
        activeChaoticSpeeds = false;
        activeNucleusOpacity = 0.7;
        break;
      case 'neutral':
      default:
        activeSpeedMultiplier = 1.0 * speedMultiplier;
        activeChaoticSpeeds = false;
        break;
    }
  }

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext("2d");
    if (!ctx) return;

    const dpr = window.devicePixelRatio || 1;
    const cssSize = Math.max(1, size);
    canvas.width = Math.floor(cssSize * dpr);
    canvas.height = Math.floor(cssSize * dpr);
    canvas.style.width = `${cssSize}px`;
    canvas.style.height = `${cssSize}px`;
    ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
    const scale = cssSize / 112;
    const nucleusRadius = 22 * scale;
    const centerX = cssSize / 2;
    const centerY = cssSize / 2;

    const elementN = Math.max(1, Math.min(118, Math.round(activeAvatarState.elementN || 1)));
    const shells = getElementByAtomicNumber(elementN)?.shells ?? [1];

    // Cap display shells at 4 max for visual rendering
    const displayShells = shells.slice(0, 4);

    const minR = cssSize * 0.15; // 30% of avatar radius
    const maxR = cssSize * 0.425; // 85% of avatar radius

    const shellRadii: number[] = [];
    for (let i = 0; i < displayShells.length; i++) {
      if (displayShells.length === 1) {
        shellRadii.push(maxR);
      } else {
        const step = (maxR - minR) / (displayShells.length - 1);
        shellRadii.push(minR + step * i);
      }
    }

    const baseSpeeds = shellRadii.map((r, i) => {
      const direction = i % 2 === 0 ? 1 : -1;
      const chaoticFactor = activeChaoticSpeeds ? (1.5 + (Math.sin(i * 1.7) + 1) * 2) : (i * 0.3 + 0.7);
      return direction * (1 / (r * 0.02)) * chaoticFactor;
    });

    const electronRadius = 4 * scale;

    const render = (timestamp: number) => {
      ctx.clearRect(0, 0, cssSize, cssSize);

      // Draw orbits
      ctx.lineWidth = 1 * scale;
      for (let i = 0; i < displayShells.length; i++) {
        ctx.beginPath();
        ctx.arc(centerX, centerY, shellRadii[i], 0, Math.PI * 2);
        ctx.strokeStyle = withHexAlpha(activeAvatarState.color, "40");
        ctx.stroke();
      }

      // Draw electrons orbiting along the circular paths
      for (let shellIdx = 0; shellIdx < displayShells.length; shellIdx++) {
        const numElectrons = displayShells[shellIdx];
        const radius = shellRadii[shellIdx];
        const speed = baseSpeeds[shellIdx];

        for (let e = 0; e < numElectrons; e++) {
          const angle = (timestamp / 1000) * speed * activeSpeedMultiplier + (e * 2 * Math.PI) / numElectrons;
          const x = centerX + Math.cos(angle) * radius;
          const y = centerY + Math.sin(angle) * radius;

          // Electron outer glowing radial gradient
          const gradient = ctx.createRadialGradient(x, y, 0, x, y, electronRadius * 2);
          gradient.addColorStop(0, activeAvatarState.color);
          gradient.addColorStop(1, "transparent");
          ctx.fillStyle = gradient;
          ctx.beginPath();
          ctx.arc(x, y, electronRadius * 2, 0, Math.PI * 2);
          ctx.fill();

          // Electron inner solid core
          ctx.fillStyle = "#ffffff";
          ctx.beginPath();
          ctx.arc(x, y, electronRadius, 0, Math.PI * 2);
          ctx.fill();
        }
      }




      const nColor = activeNucleusColor;
      const baseAlpha = ctx.globalAlpha;
      if (activeNucleusOpacity !== undefined) {
        ctx.globalAlpha = baseAlpha * activeNucleusOpacity;
      }

      // Nucleus glow
      const nucleusGradient = ctx.createRadialGradient(centerX, centerY, 0, centerX, centerY, nucleusRadius * 2);
      nucleusGradient.addColorStop(0, nColor);
      nucleusGradient.addColorStop(0.5, withHexAlpha(nColor, "cc"));
      nucleusGradient.addColorStop(1, "transparent");
      ctx.fillStyle = nucleusGradient;
      ctx.beginPath();
      ctx.arc(centerX, centerY, nucleusRadius * 2, 0, Math.PI * 2);
      ctx.fill();

      // Nucleus
      ctx.fillStyle = nColor;
      ctx.beginPath();
      ctx.arc(centerX, centerY, nucleusRadius, 0, Math.PI * 2);
      ctx.fill();

      ctx.globalAlpha = baseAlpha;

      if (!hideFace) {
        const emotion = typeof state === 'string' ? state : 'neutral';
        const eyeOffsetX = 7 * scale;
        const eyeOffsetY = -3 * scale;
        const eyeSize = 4 * scale;

        ctx.fillStyle = "#ffffff";
        ctx.strokeStyle = "#1a1a1a";
        ctx.lineWidth = 2 * scale;
        ctx.lineCap = "round";

        if (emotion === 'happy' || emotion === 'excited') {
          // Happy squint curves for eyes
          ctx.beginPath();
          ctx.arc(centerX - eyeOffsetX, centerY + eyeOffsetY + 1 * scale, 3 * scale, Math.PI, 0, false);
          ctx.stroke();

          ctx.beginPath();
          ctx.arc(centerX + eyeOffsetX, centerY + eyeOffsetY + 1 * scale, 3 * scale, Math.PI, 0, false);
          ctx.stroke();

          // Open happy smile
          ctx.fillStyle = "#1a1a1a";
          ctx.beginPath();
          ctx.arc(centerX, centerY + 1 * scale, 5 * scale, 0, Math.PI, false);
          ctx.fill();
        } 
        else if (emotion === 'sad') {
          // Sad drooping eyes
          ctx.beginPath();
          ctx.arc(centerX - eyeOffsetX, centerY + eyeOffsetY + 3 * scale, 3 * scale, 0, Math.PI, true);
          ctx.stroke();

          ctx.beginPath();
          ctx.arc(centerX + eyeOffsetX, centerY + eyeOffsetY + 3 * scale, 3 * scale, 0, Math.PI, true);
          ctx.stroke();

          // Sad frown mouth
          ctx.beginPath();
          ctx.arc(centerX, centerY + 6 * scale, 4 * scale, Math.PI, 0, false);
          ctx.stroke();
        } 
        else if (emotion === 'thinking') {
          // Thinking face
          // Left eye: horizontal line
          ctx.beginPath();
          ctx.moveTo(centerX - eyeOffsetX - 3 * scale, centerY + eyeOffsetY);
          ctx.lineTo(centerX - eyeOffsetX + 3 * scale, centerY + eyeOffsetY);
          ctx.stroke();

          // Right eye: normal circle
          ctx.fillStyle = "#ffffff";
          ctx.beginPath();
          ctx.arc(centerX + eyeOffsetX, centerY + eyeOffsetY, eyeSize, 0, Math.PI * 2);
          ctx.fill();
          ctx.fillStyle = "#1a1a1a";
          ctx.beginPath();
          ctx.arc(centerX + eyeOffsetX, centerY + eyeOffsetY, eyeSize / 2, 0, Math.PI * 2);
          ctx.fill();

          // Mouth: straight line
          ctx.beginPath();
          ctx.moveTo(centerX - 4 * scale, centerY + 3 * scale);
          ctx.lineTo(centerX + 4 * scale, centerY + 3 * scale);
          ctx.stroke();
        }
        else if (emotion === 'celebrating') {
          // Wide open happy eyes
          ctx.fillStyle = "#ffffff";
          ctx.beginPath();
          ctx.arc(centerX - eyeOffsetX, centerY + eyeOffsetY, eyeSize * 1.2, 0, Math.PI * 2);
          ctx.fill();
          ctx.beginPath();
          ctx.arc(centerX + eyeOffsetX, centerY + eyeOffsetY, eyeSize * 1.2, 0, Math.PI * 2);
          ctx.fill();

          ctx.fillStyle = "#1a1a1a";
          ctx.beginPath();
          ctx.arc(centerX - eyeOffsetX, centerY + eyeOffsetY, eyeSize * 0.6, 0, Math.PI * 2);
          ctx.fill();
          ctx.beginPath();
          ctx.arc(centerX + eyeOffsetX, centerY + eyeOffsetY, eyeSize * 0.6, 0, Math.PI * 2);
          ctx.fill();

          // Huge open mouth smile
          ctx.fillStyle = "#1a1a1a";
          ctx.beginPath();
          ctx.arc(centerX, centerY + 1 * scale, 7 * scale, 0, Math.PI, false);
          ctx.fill();
        }
        else {
          // Default neutral face
          // Left eye
          ctx.fillStyle = "#ffffff";
          ctx.beginPath();
          ctx.arc(centerX - eyeOffsetX, centerY + eyeOffsetY, eyeSize, 0, Math.PI * 2);
          ctx.fill();
          // Right eye
          ctx.beginPath();
          ctx.arc(centerX + eyeOffsetX, centerY + eyeOffsetY, eyeSize, 0, Math.PI * 2);
          ctx.fill();

          // Pupils
          ctx.fillStyle = "#1a1a1a";
          ctx.beginPath();
          ctx.arc(centerX - eyeOffsetX + 1 * scale, centerY + eyeOffsetY, 2 * scale, 0, Math.PI * 2);
          ctx.fill();
          ctx.beginPath();
          ctx.arc(centerX + eyeOffsetX + 1 * scale, centerY + eyeOffsetY, 2 * scale, 0, Math.PI * 2);
          ctx.fill();

          // Glare dots
          ctx.fillStyle = "#ffffff";
          ctx.beginPath();
          ctx.arc(centerX - eyeOffsetX, centerY + eyeOffsetY - 1 * scale, 1.5 * scale, 0, Math.PI * 2);
          ctx.fill();
          ctx.beginPath();
          ctx.arc(centerX + eyeOffsetX, centerY + eyeOffsetY - 1 * scale, 1.5 * scale, 0, Math.PI * 2);
          ctx.fill();

          // Mouth
          ctx.beginPath();
          ctx.arc(centerX, centerY + 2 * scale, 6 * scale, 0.2 * Math.PI, 0.8 * Math.PI);
          ctx.stroke();
        }
      }

      if (activeAvatarState.accessory && activeAvatarState.accessory !== "None") {
        drawAccessory(ctx, activeAvatarState.accessory, centerX, centerY, nucleusRadius, activeAvatarState.color);
      }

      if (animated) {
        rafRef.current = requestAnimationFrame(render);
      }
    };

    rafRef.current = requestAnimationFrame(render);
    return () => {
      if (rafRef.current) cancelAnimationFrame(rafRef.current);
    };
  }, [
    animated,
    size,
    activeAvatarState.accessory,
    activeAvatarState.color,
    activeAvatarState.elementN,
    activeSpeedMultiplier,
    activeChaoticSpeeds,
    activeNucleusColor,
    activeNucleusOpacity,
    hideFace,
    state
  ]);

  const mergedClassName = `block rounded-full ${className}`.trim();
  return <canvas ref={canvasRef} className={mergedClassName} />;
}
